/**
 * A **Transcript** read as turns.
 *
 * One column, one engine since #118 (CONTEXT.md): Gemini over the
 * **Call Recording**, asked to label the speakers by the call's source
 * — Caller and Operator on an answered call, Caller and Receptionist on
 * an AI-answered one, and a **Voicemail**'s one voice as Caller. A
 * Receptionist call was assembled turn by turn from the **Sideband**
 * until then, and every Transcript from before #118 reads as it did.
 *
 * A call transcribed since #118 carries its turns beside the text, each
 * with where it starts on the recording, and those are read as they
 * are (`timedTurns`). Everything older has text alone, so turns are
 * read out of the text and never required of it (`readTurns`). A
 * transcript that carries no labels comes back as no turns at all, and
 * the screen renders what it was given — which is the right rendering of
 * a voicemail anyway, because a message left on a machine is not a
 * conversation and should not be drawn as one.
 *
 * Nothing here rewrites what was said. The label is the only thing
 * translated, on the same rule the rest of this screen follows: the
 * words a Company reads are the Company's, and "Operator" is a word this
 * repo uses to describe an actor rather than to name one to somebody.
 */
import type { TranscriptTurn } from "./arrivals-api";

/** One speaker's turn, as the screen draws it. */
export interface Turn {
  /**
   * Which side was talking, and the whole of the visual difference
   * between them. `unknown` is a label neither engine was asked for, and
   * it reads at the caller's weight rather than the Company's: the
   * Customer describing their job is the half of a call worth reading,
   * and a guess must not be the reason it is the quiet one.
   */
  side: "caller" | "company" | "unknown";
  /** What to call the speaker, in the Company's words. */
  speaker: string;
  /** What they said, newlines intact. */
  text: string;
  /**
   * Where it starts on the recording, in milliseconds, on a turn the
   * transcriber timed (#118). Absent on a turn read out of the text.
   */
  startMs?: number;
  /**
   * Where it ends, on a turn the transcriber gave an end. Absent on
   * one it did not, which runs to the next turn's start.
   */
  endMs?: number;
}

/**
 * The labels the two engines write, and what a Company calls them.
 *
 * "Receptionist" is the Company's own word for the thing that answered.
 * "Operator" is not: it names an actor in a workflow and never a person,
 * so on a screen somebody reads it is their team.
 */
const SPEAKERS: Record<string, { side: Turn["side"]; speaker: string }> = {
  caller: { side: "caller", speaker: "Caller" },
  customer: { side: "caller", speaker: "Caller" },
  receptionist: { side: "company", speaker: "Receptionist" },
  operator: { side: "company", speaker: "Your team" },
};

/**
 * A line that opens a turn: a short name, a colon, then what was said.
 *
 * Short because a label is a name and not a sentence, which is most of
 * what keeps an ordinary spoken line carrying a colon from being read as
 * a speaker.
 */
const LABELLED = /^\s*([A-Za-z][A-Za-z0-9 .'-]{0,23}?):\s*(.*)$/;

/**
 * The transcript as turns, or nothing where it has none.
 *
 * A label the engines write is believed outright. A label neither of
 * them was asked for is believed when it opens more than one line, or
 * when it is one of several such labels — an engine that named the
 * speakers itself named a cast, and a sentence that happens to carry a
 * colon is one aside on its own. Everything else is the previous speaker
 * still talking, which is also what a wrapped line is, so neither is
 * lost.
 */
/**
 * The transcriber's own turns (#118), each with its time. The label is
 * translated exactly as a label read out of the text is; nothing is
 * merged, because each turn has a time of its own to keep. Nothing
 * where there are none.
 */
export function timedTurns(turns: TranscriptTurn[] | null): Turn[] {
  if (!turns) return [];
  const read: Turn[] = [];
  for (const turn of turns) {
    const text = turn.text.trim();
    if (text === "") continue;
    const said = SPEAKERS[turn.speaker.trim().toLowerCase()] ?? {
      side: "unknown" as const,
      speaker: turn.speaker.trim(),
    };
    const own: Turn = { ...said, text, startMs: turn.start_ms };
    if (turn.end_ms) own.endMs = turn.end_ms;
    read.push(own);
  }
  return read;
}

export function readTurns(transcript: string): Turn[] {
  const lines = transcript.split("\n");
  const counts = new Map<string, number>();
  for (const line of lines) {
    const label = LABELLED.exec(line)?.[1]?.trim().toLowerCase();
    if (label) counts.set(label, (counts.get(label) ?? 0) + 1);
  }
  const cast = [...counts.keys()].filter((label) => !SPEAKERS[label]).length;

  const turns: Turn[] = [];
  for (const line of lines) {
    const match = LABELLED.exec(line);
    const label = match?.[1]?.trim().toLowerCase() ?? "";
    const known = Boolean(SPEAKERS[label]);
    if (match && (known || (counts.get(label) ?? 0) > 1 || cast > 1)) {
      const said = SPEAKERS[label] ?? {
        side: "unknown" as const,
        // The label as it was written, which is all anybody knows about
        // a speaker the engine named itself.
        speaker: match[1].trim(),
      };
      const last = turns[turns.length - 1];
      // One speaker's consecutive lines are one turn: the engine broke
      // them, the conversation did not.
      if (last && last.speaker === said.speaker) {
        last.text = join(last.text, match[2]);
        continue;
      }
      turns.push({ ...said, text: match[2].trim() });
      continue;
    }
    if (turns.length === 0) {
      // Anything before the first speaker is unattributed, and dropping
      // it would lose what was said.
      if (line.trim() === "") continue;
      turns.push({ side: "unknown", speaker: "", text: line.trim() });
      continue;
    }
    turns[turns.length - 1].text = join(turns[turns.length - 1].text, line);
  }

  const said = turns.filter((turn) => turn.text !== "");
  // A transcript nobody labelled is not a conversation this can draw:
  // one voice, no turns, and the screen renders the text it was given.
  return said.some((turn) => turn.speaker !== "") ? said : [];
}

function join(text: string, next: string): string {
  const trimmed = next.trim();
  if (trimmed === "") return text;
  return text === "" ? trimmed : `${text}\n${trimmed}`;
}
